/**
 * TanStack Query mutation hooks for the Talent Pool feature.
 *
 * Follows the useAddPsychometricSection pattern (PATTERNS.md lines 138-149):
 * useMutation + queryClient.invalidateQueries on success + toast.success/error.
 *
 * Each mutation fetches an Auth0 access token via getAccessTokenSilently() and
 * passes it to the service (forwarded as a Bearer header). Without it the
 * backend returns 401 and the global handler force-logs-out the user.
 *
 * Hooks: useAssignToJob, useAddComment, useUpdateSkills, useUpdateExperience, useUpdateEducation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import type { TalentJobAssignment, TalentSkill, TalentExperience, TalentEducation } from '../types/talent.types';
import {
  assignToJob,
  addComment,
  updateSkills,
  updateExperience,
  updateEducation,
  deleteTalent,
} from '../services/talentPool.service';
import { talentKeys } from './useTalentPool';

// ── useAssignToJob ─────────────────────────────────────────────────────────────

export interface AssignToJobVariables {
  jobId: string;
}

/**
 * Assign a talent to a job. Invalidates the talent's detail and the lists cache
 * (jobsCount and latestStatus change on the list row).
 */
export const useAssignToJob = (talentId: string) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<TalentJobAssignment, Error, AssignToJobVariables>({
    mutationFn: async ({ jobId }) => {
      const token = await getAccessTokenSilently();
      const response = await assignToJob(token, { talentId, jobId });
      if (response.error) throw new Error(response.error);
      return response.data as TalentJobAssignment;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: talentKeys.detail(talentId) });
      void queryClient.invalidateQueries({ queryKey: talentKeys.lists() });
      toast.success(i18n.t('talentPool:toasts.assigned'));
    },
    onError: () => {
      toast.error(i18n.t('talentPool:toasts.assign_failed'));
    },
  });
};

// ── useBulkAssignToJob ───────────────────────────────────────────────────────────

export interface BulkAssignToJobVariables {
  talentIds: string[];
}

/**
 * Assign several talents to a single job in one action (Add-from-pool multi-select).
 * Scoped to the jobId so the assigned-talents and lists caches refresh once at the
 * end. Each assignment hits the same service used by useAssignToJob; failures for an
 * individual talent are swallowed so a partial selection still completes.
 */
export const useBulkAssignToJob = (jobId: string) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<number, Error, BulkAssignToJobVariables>({
    mutationFn: async ({ talentIds }) => {
      const token = await getAccessTokenSilently();
      let assigned = 0;
      for (const talentId of talentIds) {
        const response = await assignToJob(token, { talentId, jobId });
        if (!response.error) {
          assigned += 1;
          void queryClient.invalidateQueries({ queryKey: talentKeys.detail(talentId) });
        }
      }
      return assigned;
    },
    onSuccess: (assigned) => {
      void queryClient.invalidateQueries({ queryKey: talentKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: talentKeys.assignedTalentsForJob(jobId),
      });
      // Every assignment failed — report failure rather than a misleading success.
      if (assigned === 0) {
        toast.error(i18n.t('talentPool:toasts.assign_failed'));
        return;
      }
      toast.success(i18n.t('talentPool:toasts.assigned'));
    },
    onError: () => {
      toast.error(i18n.t('talentPool:toasts.assign_failed'));
    },
  });
};

// ── useAddComment ──────────────────────────────────────────────────────────────

export interface AddCommentVariables {
  body: string;
}

/**
 * Add a comment to a talent. Invalidates the talent's detail query so the Notes
 * and Activity tabs refresh automatically.
 */
export const useAddComment = (talentId: string) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<void, Error, AddCommentVariables>({
    mutationFn: async ({ body }) => {
      const token = await getAccessTokenSilently();
      const response = await addComment(token, { talentId, body });
      if (response.error) throw new Error(response.error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: talentKeys.detail(talentId) });
      toast.success(i18n.t('talentPool:toasts.comment_added'));
    },
    onError: () => {
      toast.error(i18n.t('talentPool:toasts.comment_failed'));
    },
  });
};

// ── useUpdateSkills ────────────────────────────────────────────────────────────

export interface UpdateSkillsVariables {
  technicalSkills: TalentSkill[];
  softSkills: TalentSkill[];
}

/**
 * Update a talent's skills. Invalidates the talent's detail and lists cache.
 */
export const useUpdateSkills = (talentId: string) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<void, Error, UpdateSkillsVariables>({
    mutationFn: async ({ technicalSkills, softSkills }) => {
      const token = await getAccessTokenSilently();
      const response = await updateSkills(token, { talentId, technicalSkills, softSkills });
      if (response.error) throw new Error(response.error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: talentKeys.detail(talentId) });
      void queryClient.invalidateQueries({ queryKey: talentKeys.lists() });
      toast.success(i18n.t('talentPool:toasts.profile_updated'));
    },
    onError: () => {
      toast.error(i18n.t('talentPool:toasts.profile_update_failed'));
    },
  });
};

// ── useUpdateExperience ────────────────────────────────────────────────────────

export interface UpdateExperienceVariables {
  experiences: TalentExperience[];
}

/**
 * Replace a talent's experience entries. Invalidates the talent detail cache.
 */
export const useUpdateExperience = (talentId: string) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<void, Error, UpdateExperienceVariables>({
    mutationFn: async ({ experiences }) => {
      const token = await getAccessTokenSilently();
      const response = await updateExperience(token, { talentId, experiences });
      if (response.error) throw new Error(response.error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: talentKeys.detail(talentId) });
      toast.success(i18n.t('talentPool:toasts.profile_updated'));
    },
    onError: () => {
      toast.error(i18n.t('talentPool:toasts.profile_update_failed'));
    },
  });
};

// ── useUpdateEducation ─────────────────────────────────────────────────────────

export interface UpdateEducationVariables {
  education: TalentEducation[];
}

/**
 * Replace a talent's education entries. Invalidates the talent detail cache.
 */
export const useUpdateEducation = (talentId: string) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<void, Error, UpdateEducationVariables>({
    mutationFn: async ({ education }) => {
      const token = await getAccessTokenSilently();
      const response = await updateEducation(token, { talentId, education });
      if (response.error) throw new Error(response.error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: talentKeys.detail(talentId) });
      toast.success(i18n.t('talentPool:toasts.profile_updated'));
    },
    onError: () => {
      toast.error(i18n.t('talentPool:toasts.profile_update_failed'));
    },
  });
};

// ── useDeleteTalent ──────────────────────────────────────────────────────────

/**
 * Delete a talent (from the talent side). Cascades on the backend to this
 * tenant's candidacies. On success the caller should navigate back to the pool;
 * we invalidate the lists cache and drop the now-stale detail query.
 */
export const useDeleteTalent = (talentId: string) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await deleteTalent(token, talentId);
      if (response.error) throw new Error(response.error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: talentKeys.lists() });
      queryClient.removeQueries({ queryKey: talentKeys.detail(talentId) });
      toast.success(i18n.t('talentPool:toasts.deleted'));
    },
    onError: () => {
      toast.error(i18n.t('talentPool:toasts.delete_failed'));
    },
  });
};
